// ModScheduleInfo.tsx
import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";

import { Button, Checkbox, Form, Icon, Input } from "semantic-ui-react";

import moment from "moment";

// import DateTextBox from "react-lib/apps/common/DateTextBox";
import LoadingIcon from "react-lib/apps/HISV3/HCU/LoadingIcon";

import {
  generateGroupedDateStrings,
  getFormattedEventItems,
  GroupedScheduleEventType,
  ScheduleInfoType,
} from "./sequence/ScheduleSetting";

import { convertAdBeDateToMoment } from "react-lib/utils/dateUtils";

export type ModScheduleInfoProps = {
  data: GroupedScheduleEventType | null;
  // disabledDate: boolean;
  eventTime?: { end: Date; start: Date };
  open: boolean;
  toolbars?: boolean;
  onClose: () => void;
  onDelete: (data: ScheduleInfoType) => Promise<void>;
  onEdit: (data: ScheduleInfoType) => Promise<void>;
  onEditingChange?: (isEditing: boolean) => void;
  onOpenMethodModal?: () => void;
  onPublish: (ids: number[]) => Promise<void>;
  onSave: (data: ScheduleInfoType) => Promise<void>;
  onTimeChange?: (startTime: string, endTime: string) => void;
};

const IMAGES = {
  close: "/static/images/schedule/close-schedule-icon.png",
  edit: "/static/images/schedule/edit-schedule-icon.png",
  editActive: "/static/images/schedule/edit-schedule-active-icon.png",
  published: "/static/images/schedule/published-schedule-icon.png",
  trash: "/static/images/schedule/trash-schedule-icon.png",
};

type Styles = Record<"icon" | "toolbars", CSSProperties>;

const styles: Styles = {
  icon: { cursor: "pointer", height: "24px", width: "auto" },
  toolbars: {
    alignItems: "center",
    display: "flex",
    gap: "1.25rem",
    justifyContent: "flex-end",
    marginBottom: "1.5rem",
  },
};

type ScheduleFormData = {
  date: string;
  end_time: string;
  is_closed?: boolean;
  start_time: string;
};

type LoadingAction = "DELETE" | "PUBLISH" | "SAVE" | null;

const ModScheduleInfo = (props: ModScheduleInfoProps) => {
  const [formData, setFormData] = useState<ScheduleFormData>({
    date: "",
    end_time: "",
    start_time: "",
  });
  const [prevFormData, setPrevFormData] = useState<ScheduleFormData>({
    date: "",
    end_time: "",
    start_time: "",
  });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isReschedule, setIsReschedule] = useState(false);
  const [loadingAction, setLoadingAction] = useState<LoadingAction>(null);

  const updateSourceRef = useRef<"props" | "resize" | "user" | null>(null);
  const lastEventTimeRef = useRef<string>("");

  useEffect(() => {
    if (!props.data || !props.open) {
      return;
    }

    const sortedEvents = [...props.data.items].sort(
      (a, b) => moment(b.start_datetime).valueOf() - moment(a.start_datetime).valueOf()
    );

    const start = moment(sortedEvents[0].start_datetime);
    const end = moment(sortedEvents[0].end_datetime);

    updateSourceRef.current = "props";

    setFormData({
      date: start.format("DD/MM/YYYY"),
      end_time: end.format("HH:mm"),
      is_closed: props.data.is_closed,
      start_time: start.format("HH:mm"),
    });
    setIsReschedule(false);
    setIsEditing(!props.toolbars);
  }, [props.data, props.open]);

  useEffect(() => {
    if (!props.eventTime) {
      return;
    }

    const start = moment(props.eventTime.start);
    const end = moment(props.eventTime.end);

    const eventTimeKey = `${start.format("HH:mm")}-${end.format("HH:mm")}`;

    if (eventTimeKey === lastEventTimeRef.current) {
      return;
    }

    lastEventTimeRef.current = eventTimeKey;
    updateSourceRef.current = "resize";

    setFormData((prev) => ({
      ...prev,
      end_time: end.format("HH:mm"),
      start_time: start.format("HH:mm"),
    }));
  }, [props.eventTime]);

  useEffect(() => {
    if (props.onEditingChange) {
      props.onEditingChange(isEditing);
    }
  }, [isEditing]);

  useEffect(() => {
    if (updateSourceRef.current === "props" || updateSourceRef.current === "resize") {
      updateSourceRef.current = null;

      return;
    }

    if (props.onTimeChange && formData.start_time && formData.end_time) {
      const eventTimeKey = `${formData.start_time}-${formData.end_time}`;

      if (eventTimeKey !== lastEventTimeRef.current) {
        lastEventTimeRef.current = eventTimeKey;
        props.onTimeChange(formData.start_time, formData.end_time);
      }
    }
  }, [formData.end_time, formData.start_time]);

  const eventIds = useMemo(() => (props.data?.items ?? []).map((item) => item.id), [props.data]);

  const isAllPublished = useMemo(
    () => (props.data?.items ?? []).every((item) => item.is_published),
    [props.data]
  );

  const isPastDate = useMemo(() => {
    if (!formData.date) {
      return true;
    }

    const birthdateAd = convertAdBeDateToMoment(formData.date);

    if (!birthdateAd) {
      return true;
    }

    const datetime = `${formData.date} ${formData.start_time}`;

    return moment(datetime, "DD/MM/YYYY HH:mm").isBefore(moment());
  }, [formData.date, formData.start_time]);

  const disabledEdit = useMemo(
    () => isPastDate || formData.is_closed,
    [formData.is_closed, isPastDate]
  );

  const formattedEventItems = useMemo(() => {
    if (props.data && props.eventTime) {
      return getFormattedEventItems(props.data.items, props.eventTime);
    }

    return props.data?.items ?? [];
  }, [props.data, props.eventTime]);

  const handleSave = async (e: SyntheticEvent) => {
    e.stopPropagation();

    if (!formData.date || !formData.start_time || !formData.end_time) {
      return;
    }

    const dateObj = moment(formData.date, "DD/MM/YYYY");

    const data = {
      ...formData,
      date: dateObj,
    };

    //  ถ้ามี appointment ให้เปิด modal แทน
    if (props.data?.has_appointment && !formData.is_closed) {
      if (props.onOpenMethodModal) {
        props.onOpenMethodModal();
      }

      return;
    }

    setLoadingAction("SAVE");

    await (props.toolbars
      ? props.onEdit({ ...data, ids: eventIds, is_reschedule: isReschedule })
      : props.onSave(data));

    setLoadingAction(null);

    if (data.is_closed) {
      props.onClose();
    }
  };

  const handleEditSchedule = () => {
    if (disabledEdit) {
      return;
    }

    if (isEditing) {
      setFormData({ ...prevFormData });
    } else {
      setPrevFormData({ ...formData });
    }

    setIsEditing(!isEditing);
  };

  const handleChangeValue = (field: keyof ScheduleFormData, value: boolean | string) => {
    updateSourceRef.current = "user";
    setFormData((prev) => ({ ...prev, [field]: value as any }));
  };

  const handleDelete = async () => {
    setLoadingAction("DELETE");

    await props.onDelete({ ...formData, date: moment(formData.date), ids: eventIds });

    setLoadingAction(null);
  };

  const handlePublishSchedule = async () => {
    setLoadingAction("PUBLISH");

    await props.onPublish(eventIds);

    setLoadingAction(null);
  };

  const handleChangeClose = (e: any, data: any) => {
    handleChangeValue("is_closed", !!data.checked);

    setIsReschedule(!!data.checked);
  };

  const isValid = formData.date && formData.start_time && formData.end_time;

  return (
    <Form style={{ minWidth: "17.5rem" }} size="small">
      {props.toolbars && (
        <div style={styles.toolbars}>
          {props.data?.has_provider && (
            <>
              <Checkbox
                className="black"
                checked={formData.is_closed ?? false}
                disabled={eventIds.length > 1 || isPastDate}
                label="Close"
                onChange={handleChangeClose}
              />
              <div style={{ flex: 1 }} />
            </>
          )}
          {!disabledEdit && (
            <img
              style={{ ...styles.icon }}
              alt="edit.icon"
              src={isEditing ? IMAGES.editActive : IMAGES.edit}
              onClick={handleEditSchedule}
            />
          )}
          {!props.data?.has_provider && (
            <LoadingIcon
              style={styles.icon}
              dimmerStyle={{ marginLeft: 0 }}
              img={IMAGES.trash}
              loading={loadingAction === "DELETE"}
              onClick={handleDelete}
            />
          )}
          {!isAllPublished && (
            <LoadingIcon
              style={{ ...styles.icon, marginTop: "-1px" }}
              dimmerStyle={{ marginLeft: 0 }}
              img={IMAGES.published}
              loading={loadingAction === "PUBLISH"}
              onClick={handlePublishSchedule}
            />
          )}
          <img
            style={styles.icon}
            alt="close.icon"
            src={IMAGES.close}
            onClick={() => {
              props.onClose();
            }}
          />
        </div>
      )}
      {/* Date Field */}
      <Form.Field style={{ marginBottom: "1rem" }}>
        <div style={{ alignItems: "center", display: "flex", gap: "12px" }}>
          <Icon
            style={{
              color: "#FF69B4",
              fontSize: "20px",
              margin: 0,
            }}
            name="calendar alternate outline"
          />
          {isEditing && (
            <Input
              style={{ flex: 1 }}
              readOnly
              input={{
                style: {
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                },
              }}
              value={
                generateGroupedDateStrings(formattedEventItems)
                  .full_date_first_month_only_without_past
              }
            />
          )}
          {/* {isEditing && !props.disabledDate && (
            <DateTextBox
              adFormat="DD/MM/YYYY"
              value={formData.date}
              autoAdBe
              disabled
              useAdDate
              setLanguage="en"
              onChange={(date) => {
                handleChangeValue("date", date);
              }}
              input={{
                style: {
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                },
              }}
            />
          )} */}
          {!isEditing && (
            <label style={{ fontSize: "1rem" }}>{props.data?.full_date_first_month_only}</label>
          )}
        </div>
      </Form.Field>

      {/* Time Fields */}
      <Form.Field style={{ marginBottom: "1rem" }}>
        <div style={{ alignItems: "center", display: "flex", gap: "12px" }}>
          <Icon
            style={{
              color: "#FF69B4",
              fontSize: "20px",
              margin: 0,
            }}
            name="clock outline"
          />
          <div
            style={{
              alignItems: "center",
              display: "flex",
              flex: 1,
              gap: "8px",
              minWidth: "max-content",
            }}
          >
            {isEditing ? (
              <Input
                style={{ flex: 1 }}
                type="time"
                value={formData.start_time}
                onChange={(e) => handleChangeValue("start_time", e.target.value)}
                input={{
                  style: {
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                  },
                }}
              />
            ) : (
              <label style={{ fontSize: "1rem" }}>
                {moment(formData.start_time, "HH:mm").format("hh:mm A")}
              </label>
            )}
            <span style={{ color: "#666" }}>-</span>
            {isEditing ? (
              <Input
                style={{ flex: 1 }}
                type="time"
                value={formData.end_time}
                onChange={(e) => handleChangeValue("end_time", e.target.value)}
                input={{
                  style: {
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                  },
                }}
              />
            ) : (
              <label style={{ fontSize: "1rem" }}>
                {moment(formData.end_time, "HH:mm").format("hh:mm A")}
              </label>
            )}
          </div>
        </div>
      </Form.Field>

      {formData.is_closed && (
        <>
          <Form.Field style={{ margin: "-0.25rem 0 0.5rem" }}>
            <div style={{ color: "rgba(200, 85, 120, 1)", fontWeight: "500" }}>
              Changing service hours may affect provider schedules and currently booking.
            </div>
          </Form.Field>
          <Form.Field>
            <Checkbox
              className="black"
              checked={isReschedule}
              label="Reschedule all appointments"
            />
          </Form.Field>
        </>
      )}

      {/* Save Button */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {(isEditing || formData.is_closed) && (
          <Button
            style={{
              backgroundColor: "var(--primary-theme-color)",
              border: "none",
              borderRadius: "6px",
              color: "white",
            }}
            disabled={!isValid || isPastDate}
            loading={loadingAction === "SAVE"}
            onClick={handleSave}
          >
            Save
          </Button>
        )}
      </div>
    </Form>
  );
};

ModScheduleInfo.displayName = "ModScheduleInfo";

export default React.memo(ModScheduleInfo);
